import React, { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FetchFailed from 'reusable/FetchFailed';
import { isImeComposing } from 'reusable/isImeComposing';
import { FetchRemotePage } from './remotePage';
import { useRemoteSearch } from './useRemoteSearch';

export interface SearchComboboxProps<T> {
  /** 入力欄の id（項目名の label の htmlFor から結ぶ）。 */
  id: string;
  /** 項目名。入力欄と候補の一覧の名前。 */
  label: string;
  /** 欄の説明の id（ヘルプ・必須の知らせなど）。失敗の知らせの id は中で足す。 */
  describedBy?: string;
  placeholder?: string;
  /** true なら複数を選ぶ（選んだ項目は欄の上に並べ、外すボタンを付ける）。 */
  multiple?: boolean;
  /**
   * 選んでいる項目。候補（今の検索の結果）に無くても、ここにある名前で出す。
   * 1つを選ぶ欄では0個か1個。
   */
  selected: T[];
  /** 候補の1ページを読む（検索の文字・ページ番号）。 */
  fetchPage: FetchRemotePage<T>;
  getKey: (item: T) => string | number;
  /** 名前（選んだ項目・候補の文字・読み上げ）。空なら「-」で出す。 */
  getLabel: (item: T) => string;
  /** 候補の行の中身（既定は名前）。 */
  renderOption?: (item: T) => ReactNode;
  /** サーバーの候補より前に必ず出す候補（機器の状態など）。検索の文字で画面の中で絞る。 */
  fixedOptions?: T[];
  /** 選んだ（複数の欄では、まだ選んでいない候補を選んだ）。 */
  onSelect: (item: T) => void;
  /** 外した（複数の欄の外すボタン・選んでいる候補をもう一度選んだ）。 */
  onRemove?: (item: T) => void;
  /** 選べない（読み込み中・保存中など）。選んだ項目は出す。 */
  readOnly?: boolean;
  /** 変わったら候補を1ページ目から読み直す。 */
  resetKey?: string | number;
  invalid?: boolean;
  /** 必須の欄（aria-required。required 属性は使わない。EV-430） */
  ariaRequired?: boolean;
  boxClassName?: string;
}

/**
 * EV-493。文字を入れると候補をサーバーで検索する選ぶ欄（WAI-ARIA の combobox。一覧の候補・自動で選ばない）。
 *
 * - 候補は数十件ずつ。続きは一覧の最後の「もっと見る」（↓で移って Enter、または押す）か、一覧を下までスクロール。
 * - ↓・↑ で候補を移り（閉じていれば開く）、Enter で選ぶ（開いている間はフォームを送らない）。
 *   Escape は候補だけを閉じる（閉じていればダイアログを閉じる）。Tab は候補を閉じて次の欄へ。
 * - 1つを選ぶ欄は、選ぶと名前を入力欄に出す。選ばずに離れたら、選んでいる名前に戻す。
 * - 複数の欄は、選んでいる項目を欄の上に並べる（外すボタン付き。外したら次の外すボタンへフォーカスを移す）。
 * - 読めなかったら欄の下に既存の文言（一覧を読み込めなかった・失敗の種類）と「再読み込み」を出す。
 */
function SearchCombobox<T>({
  id,
  label,
  describedBy,
  placeholder,
  multiple = false,
  selected,
  fetchPage,
  getKey,
  getLabel,
  renderOption,
  fixedOptions = [],
  onSelect,
  onRemove,
  readOnly = false,
  resetKey,
  invalid,
  ariaRequired,
  boxClassName = '',
}: SearchComboboxProps<T>) {
  const { t } = useTranslation('common');
  const listboxId = useId();
  const errorId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // 1つを選ぶ欄で、利用者が文字を入れている間（選んだ名前ではなく入れた文字を出す）。
  const [typing, setTyping] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const pendingFocus = useRef<number | null>(null);
  // ↓↑ で選ぶ位置を見えるところまで送ったときのスクロール。続きを読む（下まで送った）とは扱わない。
  // 扱うと、「もっと見る」へ移っただけで続きを読み、Enter で別の候補を選んでしまう。
  const keyboardScroll = useRef(false);
  // ↓・↑ で開いたが、まだ候補が無い（読み込み中）。読み終えたら最初（↑ なら最後）の候補を選ぶ位置にする。
  const pendingStart = useRef<'first' | 'last' | null>(null);
  const expanded = open && !readOnly;

  const search = useRemoteSearch<T>({ fetchPage, getKey, enabled: expanded, resetKey });

  const text = search.query.trim().toLowerCase();
  const fixed = text
    ? fixedOptions.filter((option) => (getLabel(option) || '').toLowerCase().includes(text))
    : fixedOptions;
  const fixedKeys = new Set(fixedOptions.map((option) => getKey(option)));
  const options = [...fixed, ...search.items.filter((item) => !fixedKeys.has(getKey(item)))];
  const loadMoreIndex = search.status === 'loaded' && search.hasMore ? options.length : -1;
  const count = options.length + (loadMoreIndex >= 0 ? 1 : 0);
  const selectedKeys = new Set(selected.map((item) => getKey(item)));
  const single = selected[0];

  const optionId = (index: number) => `${listboxId}-${index}`;

  // 外を押したら閉じる。
  useEffect(() => {
    if (!expanded) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close(true);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // 選べなくなったら閉じる。
  useEffect(() => {
    if (readOnly) setOpen(false);
  }, [readOnly]);

  // 外したら、その位置の外すボタン（無ければ入力欄）へフォーカスを移す。
  useEffect(() => {
    const index = pendingFocus.current;
    if (index === null) return;
    pendingFocus.current = null;
    const buttons = Array.from(
      rootRef.current?.querySelectorAll<HTMLButtonElement>('[data-remove-option]') ?? []
    );
    (buttons[Math.min(index, buttons.length - 1)] ?? inputRef.current)?.focus();
  }, [selected]);

  // 選んでいる候補が見えるように一覧を送る。
  useEffect(() => {
    if (!expanded || activeIndex < 0) return;
    const element = document.getElementById(optionId(activeIndex));
    if (element?.scrollIntoView) {
      keyboardScroll.current = true;
      element.scrollIntoView({ block: 'nearest' });
      // 送らなかった（すでに見えていた）ときはスクロールが起きないので、次の描画の前に印を戻す。
      requestAnimationFrame(() => {
        keyboardScroll.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, expanded]);

  // 続きを読んだら、移る先（読む前の「もっと見る」の位置）が無くなったときだけ位置を直す。
  useEffect(() => {
    if (activeIndex >= count) setActiveIndex(count - 1);
  }, [activeIndex, count]);

  const openList = (start: 'first' | 'last' | 'none') => {
    if (readOnly) return;
    if (!multiple && !typing) search.setQuery('');
    setOpen(true);
    // 開くと候補を読み直す（useRemoteSearch）。読み終えてから位置を決める。
    pendingStart.current = start === 'none' ? null : start;
    setActiveIndex(-1);
  };

  useEffect(() => {
    if (!expanded || pendingStart.current === null || search.status !== 'loaded') return;
    const start = pendingStart.current;
    pendingStart.current = null;
    if (count > 0) setActiveIndex(start === 'first' ? 0 : count - 1);
  }, [expanded, search.status, count]);

  function close(leaving: boolean) {
    pendingStart.current = null;
    setOpen(false);
    setActiveIndex(-1);
    if (leaving && !multiple) {
      // 選ばずに離れたら、入れた文字を消して選んでいる名前に戻す。
      setTyping(false);
      search.setQuery('');
    }
  }

  const choose = (index: number) => {
    if (index === loadMoreIndex) {
      search.loadMore();
      return;
    }
    const item = options[index];
    if (item === undefined) return;
    if (multiple) {
      if (selectedKeys.has(getKey(item))) onRemove?.(item);
      else onSelect(item);
      return;
    }
    onSelect(item);
    setTyping(false);
    search.setQuery('');
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(event)) return; // EV-120
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const down = event.key === 'ArrowDown';
        if (!expanded) {
          openList(down ? 'first' : 'last');
          return;
        }
        if (count === 0) return;
        setActiveIndex((index) => {
          if (index < 0) return down ? 0 : count - 1;
          return (index + (down ? 1 : -1) + count) % count;
        });
        return;
      }
      case 'Enter': {
        if (!expanded) return; // 閉じていれば、ふだんどおり（フォームを送る）
        event.preventDefault();
        if (activeIndex >= 0) choose(activeIndex);
        return;
      }
      case 'Escape': {
        if (!expanded) return; // ダイアログを閉じる（useModalDialog）
        event.preventDefault();
        // PR #431 の Codex の P2。1つを選ぶ欄は、選ばずに閉じたら入れた文字を消して選んでいる名前に戻す
        // （閉じた欄に、保存する値と違う文字を出さない）。複数の欄は検索の文字を残す。
        close(!multiple);
        return;
      }
      case 'Tab': {
        if (expanded) close(true);
        return;
      }
      default:
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    pendingStart.current = null;
    search.setQuery(event.target.value);
    if (!multiple) setTyping(true);
    setOpen(true);
    setActiveIndex(-1);
  };

  // 一覧を下まで送ったら続きを読む（マウス・タッチの操作）。
  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (keyboardScroll.current) {
      keyboardScroll.current = false;
      return;
    }
    const element = event.currentTarget;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 8) search.loadMore();
  };

  // PR #424 の Codex の P2。候補は開いている間だけ読む。閉じた後（Tab で再読み込みへ移った・外を押した）の再読み込みは、
  // 候補を開いて1ページ目から読み直す（開くと読む）。開いていれば失敗した読み込みをやり直す。入力欄へフォーカスを戻す。
  const retry = () => {
    if (readOnly) return;
    if (expanded) search.retry();
    else {
      setOpen(true);
    }
    inputRef.current?.focus();
  };

  const remove = (index: number, item: T) => {
    pendingFocus.current = index;
    onRemove?.(item);
  };

  const inputValue = multiple || typing || expanded ? search.query : single ? getLabel(single) || '-' : '';
  const remaining =
    search.total !== undefined ? Math.max(search.total - search.items.length, 0) : undefined;
  const failed = search.status === 'failed' || Boolean(search.error);
  const describedByIds = [describedBy, failed ? errorId : null].filter(Boolean).join(' ') || undefined;

  let statusText: string | null = null;
  if (expanded) {
    if (search.status === 'loading' || search.loadingMore) statusText = t('ui.loading');
    else if (search.status === 'loaded' && options.length === 0) statusText = t('ui.no-results');
  }

  return (
    <div className="w-full relative" ref={rootRef}>
      <div className={`border border-neutral/50 rounded-md flex flex-wrap items-center gap-1 p-1 ${boxClassName}`}>
        {multiple && selected.length > 0 && (
          <ul className="flex flex-wrap items-center gap-1" aria-label={t('ui.selected-options')}>
            {selected.map((item, index) => (
              <li key={getKey(item)} className="inline-block bg-gray-200 rounded px-2">
                {/* EV-338。名前が空なら「-」（EV-311。空の項目にしない）。 */}
                {getLabel(item) || '-'}
                {!readOnly && (
                  <button
                    type="button"
                    className="ml-1 text-red-700"
                    data-remove-option
                    aria-label={`${getLabel(item) || '-'} ${t('ui.remove-option')}`}
                    onClick={() => remove(index, item)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-describedby={describedByIds}
          aria-invalid={invalid ? 'true' : undefined}
          aria-required={ariaRequired ? 'true' : undefined}
          aria-readonly={readOnly || undefined}
          readOnly={readOnly}
          autoComplete="off"
          className="flex-1 min-w-[6rem] h-9 px-2 bg-transparent focus:outline-none"
          placeholder={placeholder ?? t('ui.type-to-search')}
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          // PR #431 の Codex の P2。1つを選ぶ欄は、選ばずに欄を離れたら（Tab・外を押す）選んでいる名前に戻す。
          // 候補を押す・送れる枠に移るときは、この欄の中なので戻さない。
          onBlur={(event) => {
            if (multiple || rootRef.current?.contains(event.relatedTarget as Node | null)) return;
            if (expanded || typing) close(true);
          }}
          // 候補はフォーカスでは開かず、押す・入力する・↓ で開く（WAI-ARIA の combobox の例と同じ。ADM-16-AC2）。
          onClick={() => {
            if (!expanded) openList('none');
          }}
        />
      </div>
      {/* 一覧が閉じていても候補の一覧の要素は置く（aria-controls の先を在るようにする）。 */}
      {/* axe（scrollable-region-focusable）。送れる一覧の枠は、キーボードで届く必要がある。候補はフォーカスを
          入力欄に残して選ぶ（aria-activedescendant）ので、枠にフォーカスが来たら入力欄へ戻す（↓↑ で一覧を送れる）。
          枠は開いている間だけあり、入力欄の Tab は候補を閉じて次の欄へ移るので、Tab の順で止まることはない。 */}
      <div
        hidden={!expanded}
        tabIndex={0}
        onFocus={() => inputRef.current?.focus()}
        className="absolute z-10 mt-1 w-full bg-white border border-neutral/50 rounded-md shadow-lg max-h-60 overflow-y-auto"
        onScroll={onScroll}
      >
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
        >
          {expanded &&
            options.map((item, index) => {
              const chosen = selectedKeys.has(getKey(item));
              return (
                // マウスの操作。キーボードでは入力欄の ↓↑・Enter で選ぶ（combobox）。
                <li
                  key={getKey(item)}
                  id={optionId(index)}
                  role="option"
                  aria-selected={multiple ? chosen : index === activeIndex}
                  data-combobox-option
                  className={`cursor-pointer px-4 py-2 hover:bg-gray-200 flex items-center gap-2 ${
                    index === activeIndex ? 'bg-gray-200' : ''
                  } ${chosen ? 'text-primary/300 font-medium' : 'text-neutral/300'}`}
                  // フォーカスを入力欄に残す（候補へ移すと combobox の操作が途切れる）。
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(index)}
                >
                  {/* 複数の欄は、以前の候補（チェックボックス）と同じ見た目の印を出す（読み上げは aria-selected）。 */}
                  {multiple && (
                    <span
                      aria-hidden="true"
                      className={`w-4 h-4 shrink-0 inline-flex items-center justify-center rounded-sm border text-[10px] leading-none ${
                        chosen ? 'bg-primary/300 border-primary/300 text-white' : 'border-gray-600 bg-white'
                      }`}
                    >
                      {chosen ? '✓' : ''}
                    </span>
                  )}
                  {renderOption ? renderOption(item) : getLabel(item) || '-'}
                </li>
              );
            })}
          {expanded && loadMoreIndex >= 0 && (
            <li
              id={optionId(loadMoreIndex)}
              role="option"
              aria-selected={!multiple && loadMoreIndex === activeIndex}
              data-load-more
              data-combobox-option
              className={`cursor-pointer px-4 py-2 text-primary/300 underline hover:bg-gray-200 ${
                loadMoreIndex === activeIndex ? 'bg-gray-200' : ''
              }`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(loadMoreIndex)}
            >
              {remaining !== undefined
                ? t('ui.load-more-count', { count: remaining })
                : t('ui.load-more')}
            </li>
          )}
        </ul>
        {statusText && (
          <p role="status" className="px-4 py-2 text-gray-700">
            {statusText}
          </p>
        )}
      </div>
      {failed && (
        <FetchFailed id={errorId} compact error={search.error} onRetry={retry} />
      )}
    </div>
  );
}

export default SearchCombobox;
